import { vec2 } from 'gl-matrix';

import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { ImportFileCommand } from '@canvas/command/import-file-command';
import { commandService } from '@canvas/command/singleton';
import {
  isSupportedFileImportMimeType,
  isSupportedImageMimeType,
} from '@canvas/constants/multimedia';
import { base64Decode, base64Encode } from '@canvas/encoding';
import { positionAndSizeToBox } from '@canvas/math/box/convert';
import { boxGlobalToLocal } from '@canvas/math/box/transform';
import { vec2extended } from '@canvas/math/vec2';
import type { MediaClient } from '@canvas/media-client';
import { type Board } from '@canvas/model/board';
import { type Element, type ElementData } from '@canvas/model/element';
import {
  type AttachmentInfoPasteData,
  isAttachable,
} from '@canvas/model/elements/attachable';
import {
  type ConnectorElementData,
  connectorElementType,
} from '@canvas/model/elements/connector';
import { type PathElementData, pathElementType } from '@canvas/model/elements/path';
import {
  type PathLabelElementData,
  pathLabelElementType,
} from '@canvas/model/elements/path-label';
import {
  type PathWaypointElementData,
  pathWaypointElementType,
} from '@canvas/model/elements/path-waypoint';
import { smartConnectorElementType } from '@canvas/model/elements/smart-connector';
import {
  type SanitizeOptions,
  type Sanitizer,
  serializeCopyPayload,
} from '@canvas/model/sanitizers';
import { type ElementClipboardData } from '@canvas/model/ui';
import { type ImmutableObject } from '@canvas/model/util';
import { captureException } from '@canvas/sentry-react';
import { Vector2, vectorReviver } from '@canvas/vector';

import { type PasteOrDropPayload } from './clipboard-types.ts';
import { parseConfluenceImageFromClipboardHtml } from './confluence-image-from-clipboard.ts';
import { createConfluencePastePayload } from './confluence-image-paste.ts';
import { toConnectorStartAndEnd } from './connector-from-clipboard.ts';
import {
  arrangeImages,
  getImageFromClipboardItem,
  imageBlobToPasteData,
  imageDataFromClipboard,
  imageDataToClipboard,
  type ImagePasteData,
} from './image-from-clipboard.ts';
import {
  handleHtmlText,
  handlePlainText,
  tryParseAsSmartLink,
} from './text-from-clipboard.ts';
export { textElementDataFromPlainText } from './text-from-clipboard.ts';
export {
  imageDataFromClipboard,
  imageDataToClipboard,
  isImageCopyData,
  type ImagePasteData,
} from './image-from-clipboard.ts';
export type { ImageCopyData } from './image-from-clipboard.ts';
import {
  type ClipboardTableCellMeasurementFn,
  getRelativeCoordinate,
  getSelectionCenter,
  handleFormattedTable,
} from './utils.ts';

type PreProcessor = (data: ElementData<any>[]) => ElementData<any>[];
type IdMapper = (
  data: ElementClipboardData,
  getCopyIndex: (id: string | undefined) => number,
) => ElementClipboardData;
type CopyHtmlRepresentation = string | Node;
type CopyHtmlSerializer = (
  data: ElementData<any>[],
) => CopyHtmlRepresentation | CopyHtmlRepresentation[] | undefined;

export type PreparedCopyPayload = {
  copiedElements: ElementData<any>[];
  fileData: string[];
  origin: [number, number];
};

export const composeCopyPayload = (
  mediaClient: MediaClient,
  selectedElements: ImmutableObject<Element<any>>[],
  elements: Board['elements'] = {},
  sanitizeOpts: SanitizeOptions = { retainContent: true },
  sanitizers?: Sanitizer[],
  preprocessors: PreProcessor[] = [],
  idMapper: IdMapper = (data, _getCopyIndex) => data,
  elementsToCopy: ImmutableObject<Element<any>>[] = selectedElements,
  htmlSerializers: CopyHtmlSerializer[] = [],
) => {
  return serializePreparedCopyPayload(
    prepareCopyPayload(
      mediaClient,
      selectedElements,
      elements,
      preprocessors,
      elementsToCopy,
    ),
    elements,
    sanitizeOpts,
    sanitizers,
    idMapper,
    htmlSerializers,
  );
};

const getCopyOriginElements = <
  T extends ImmutableObject<Element<any>> | ElementData<any>,
>(
  copyElements: T[],
) => {
  return copyElements
    .filter(
      (element) =>
        !isAttachable(element) ||
        !element.attachedTo ||
        !copyElements.some(
          (copiedElement) => copiedElement.id === element.attachedTo?.parentId,
        ),
    )
    .filter((element) => element.type !== 'group');
};

const getCopyPayloadOrigin = (
  selectedElements: ImmutableObject<Element<any>>[],
  copiedElements: ElementData<any>[],
  elements: Board['elements'] = {},
): [number, number] => {
  const selectedOriginElements = getCopyOriginElements(selectedElements);
  const copiedOriginElements = getCopyOriginElements(copiedElements);
  const originElements =
    selectedOriginElements.length > 0
      ? selectedOriginElements
      : copiedOriginElements.length > 0
        ? copiedOriginElements
        : copiedElements;

  if (originElements.length === 1) {
    const [singleElement] = originElements;
    if (
      singleElement.type === pathElementType ||
      singleElement.type === connectorElementType ||
      singleElement.type === smartConnectorElementType
    ) {
      return getSelectionCenter(originElements, elements);
    }
    return [singleElement.position.x, singleElement.position.y];
  }

  return getSelectionCenter(originElements, elements);
};

const getCopySourceLayerOrigin = (
  element: ElementClipboardData,
  elements: Board['elements'],
  origin: [number, number],
): ElementClipboardData['copySourceLayerOrigin'] => {
  if (!('layerId' in element) || typeof element.layerId !== 'string') {
    return undefined;
  }

  if (!('position' in element)) {
    return undefined;
  }

  const layer = elements[element.layerId];
  if (layer === undefined || !('position' in layer) || !('size' in layer)) {
    return undefined;
  }

  const layerBounds = positionAndSizeToBox(layer.position, {
    width: layer.size.x,
    height: layer.size.y,
  });

  const originInLayer = boxGlobalToLocal(
    layerBounds,
    vec2.fromValues(origin[0], origin[1]),
  );

  return {
    layerId: element.layerId,
    originInLayer: {
      x: originInLayer[0],
      y: originInLayer[1],
    },
  };
};

export const prepareCopyPayload = (
  mediaClient: MediaClient,
  selectedElements: ImmutableObject<Element<any>>[],
  elements: Board['elements'] = {},
  preprocessors: PreProcessor[] = [],
  elementsToCopy: ImmutableObject<Element<any>>[] = selectedElements,
): PreparedCopyPayload => {
  const fileData: string[] = [];

  let copiedElements = [...elementsToCopy]
    // Sort our payload by zIndex so they'll get created in that order
    .sort((a, b) => a.zIndex - b.zIndex)
    // Process images
    .map((e) => imageDataToClipboard(mediaClient, e, fileData))
    // Ignore undefined or non-copyable elements
    // Iteration plan: https://product-fabric.atlassian.net/browse/CNS-9866
    // NOTE: Do not add more non-copyable elements here until above ticket is done.
    .filter((x): x is ElementData => x != null && x.type !== 'comment');

  for (const preprocessor of preprocessors) {
    copiedElements = preprocessor(copiedElements);
  }

  return {
    copiedElements,
    fileData,
    origin: getCopyPayloadOrigin(selectedElements, copiedElements, elements),
  };
};

export const serializePreparedCopyPayload = (
  preparedCopyPayload: PreparedCopyPayload,
  elements: Board['elements'] = {},
  sanitizeOpts: SanitizeOptions = { retainContent: true },
  sanitizers?: Sanitizer[],
  idMapper: IdMapper = (data, _getCopyIndex) => data,
  htmlSerializers: CopyHtmlSerializer[] = [],
) => {
  const { copiedElements, fileData, origin } = preparedCopyPayload;
  const copiedIds = copiedElements.map((e) => e.id);
  const getCopyIndex = (id: string | undefined) => (id ? copiedIds.indexOf(id) : -1);

  const mappedElements = copiedElements
    // Remove properties that aren't needed
    .map((element) => serializeCopyPayload(element, sanitizeOpts, sanitizers))
    .map((e) => idMapper(e, getCopyIndex))
    // Override position with relative coordinates for multi-selection
    .map((e) => {
      if (e.type === pathElementType) {
        const path = e as PathElementData;
        return {
          ...e,
          start: getRelativeCoordinate(origin, path.start),
          end: getRelativeCoordinate(origin, path.end),
        };
      }

      if (e.type === connectorElementType || e.type === smartConnectorElementType) {
        const connector = e as ConnectorElementData;

        const { sourceElement, targetElement } = connector;
        const sourceIndex = getCopyIndex(sourceElement);
        const targetIndex = getCopyIndex(targetElement);

        const { start, end } = toConnectorStartAndEnd(connector, elements);

        return {
          ...e,
          start: getRelativeCoordinate(origin, start),
          end: getRelativeCoordinate(origin, end),
          sourceIndex,
          targetIndex,
        };
      }

      if (e.type === pathLabelElementType) {
        const pathLabel = e as PathLabelElementData;
        const { sourcePathId } = pathLabel;
        const sourcePathIndex = getCopyIndex(sourcePathId);

        return {
          ...e,
          sourcePathIndex,
        };
      }

      if (e.type === pathWaypointElementType) {
        const { sourcePathId, position } = e as PathWaypointElementData;
        const sourcePathIndex = getCopyIndex(sourcePathId);

        const relativePosition = getRelativeCoordinate(
          origin,
          vec2.fromValues(position.x, position.y),
        );

        return {
          ...e,
          sourcePathIndex,
          position: new Vector2(relativePosition[0], relativePosition[1]),
        };
      }

      return {
        ...e,
        copySourceLayerOrigin: getCopySourceLayerOrigin(e, elements, origin),
        position: Vector2.fromJSON(
          getRelativeCoordinate(origin, vec2.fromValues(e.position.x, e.position.y)),
        ),
        ...('basisPosition' in e
          ? {
              basisPosition: Vector2.fromJSON(
                getRelativeCoordinate(
                  origin,
                  vec2.fromValues(
                    (e.basisPosition as Vector2).x,
                    (e.basisPosition as Vector2).y,
                  ),
                ),
              ),
            }
          : null),
        // If the element is attached to another element, and that element is being
        // copied, we need to store the index of its parent rather than its ID
        ...(isAttachable(e) && e.attachedTo && copiedIds.includes(e.attachedTo.parentId)
          ? {
              attachedTo: {
                parentIndex: copiedIds.indexOf(e.attachedTo.parentId),
                offset: vec2extended.toJSON(e.attachedTo.offset),
              } satisfies AttachmentInfoPasteData,
            }
          : {
              // If the element is attached to another element, and that element is not
              // being copied, don't copy the attachedTo property
              attachedTo: undefined,
            }),
      };
    });

  const base64Encoded = base64Encode(JSON.stringify(mappedElements));

  // TODO: Update template files to avoid using <template> tag
  // Use a <div> element to avoid Safari stripping out the <template> tag, when entered by a user, and hindering copy and paste compatibility
  const container = document.createElement('div');
  container.innerHTML = buildCanvasClipboardHtml(base64Encoded, fileData);

  let hasCustomHtmlRepresentation = false;
  for (const serializer of htmlSerializers) {
    const representation = serializer(copiedElements);
    if (representation == null) {
      continue;
    }

    const fragments = Array.isArray(representation) ? representation : [representation];
    for (const fragment of fragments) {
      if (typeof fragment === 'string') {
        if (fragment.length === 0) {
          continue;
        }
        const template = document.createElement('template');
        template.innerHTML = fragment;
        container.appendChild(template.content);
        hasCustomHtmlRepresentation = true;
        continue;
      }

      container.appendChild(fragment);
      hasCustomHtmlRepresentation = true;
    }
  }

  // We need a HTML representation for ProseMirror's paste to work properly
  if (!hasCustomHtmlRepresentation) {
    const editors = document.querySelectorAll('#canvas-editor .ProseMirror');
    for (const editor of editors) {
      for (const editorChild of editor.childNodes) {
        container.appendChild(editorChild.cloneNode(true));
      }
    }
  }

  return container.innerHTML;
};

/**
 * Writes an HTML string to the OS clipboard via the Clipboard API.
 */
export const writeHtmlToOsClipboard = (html: string): void => {
  const blob = new Blob([html], { type: 'text/html' });
  const clipboardItem = new ClipboardItem({ 'text/html': blob });

  navigator.clipboard.write([clipboardItem]).catch(console.error);
};

/**
 * Builds the canvas clipboard HTML envelope containing a base64-encoded payload.
 * Returns an HTML string with a `<div id="canvas-clipboard" data-canvas-clipboard="...">`.
 */
export const buildCanvasClipboardHtml = (base64: string, fileData?: string[]): string => {
  const container = document.createElement('div');
  const clipboard = document.createElement('div');
  clipboard.setAttribute('id', 'canvas-clipboard');
  clipboard.dataset.canvasClipboard = base64;

  // Store image file data separately to avoid encoding them twice which would be
  // wasteful
  if (fileData) {
    for (let i = 0; i < fileData.length; i++) {
      clipboard.dataset[`file${i}`] = fileData[i];
    }
  }
  container.appendChild(clipboard);
  return container.innerHTML;
};

const extractCanvasPayload = (html: string): unknown => {
  const el = new DOMParser()
    .parseFromString(html, 'text/html')
    .getElementById('canvas-clipboard');
  const encoded = el?.dataset?.canvasClipboard;
  if (!encoded) {
    return undefined;
  }
  try {
    const decoded = JSON.parse(base64Decode(encoded), vectorReviver);
    return decoded;
  } catch {
    return undefined;
  }
};
/**
 * Parses content with our canvas clipboard format.
 * @param content - The HTML string to parse.
 * @returns The parsed canvas elements, or undefined if the content is not in our format.
 */
export const parsePastePayload = (
  content: string,
): ElementClipboardData[] | undefined => {
  const parser = new DOMParser();
  const clipboardElement = parser
    .parseFromString(content, 'text/html')
    ?.getElementById('canvas-clipboard');

  if (clipboardElement?.dataset?.canvasClipboard) {
    const decodedString = base64Decode(clipboardElement?.dataset?.canvasClipboard);
    const decoded = JSON.parse(decodedString, vectorReviver);
    if (!Array.isArray(decoded)) {
      return undefined;
    }
    const elements = (decoded as ElementClipboardData[])
      // Process images
      .map((e) =>
        imageDataFromClipboard(
          e,
          (fileIndex) => clipboardElement.dataset[`file${fileIndex}`],
        ),
      )
      .filter((x): x is ElementClipboardData => x != null);
    return elements;
  }
};

/**
 * Gets a string from our clipboard by type.
 * @param item ClipboardItem that may contain data
 * @param type which slot we are retrieving from 'text/html' or 'text/plain'
 * @returns A promise containing either a raw string, or undefined if the slot was empty
 */
const getFromClipboard = async (
  item: ClipboardItem,
  type: 'text/html' | 'text/plain',
): Promise<string | undefined> => {
  const blob = item.types.includes(type) ? await item.getType(type) : undefined;
  const text = await blob?.text();
  return text;
};

export const getCanvasPayloadFromClipboardItem = async (
  item: ClipboardItem,
): Promise<ElementClipboardData[] | undefined> => {
  const contentRaw = await getFromClipboard(item, 'text/html');
  if (!contentRaw) {
    return;
  }
  return parsePastePayload(contentRaw);
};

/**
 * Reads the OS clipboard and extracts the raw base64 canvas clipboard payload.
 * Returns `undefined` if the clipboard does not contain a canvas clipboard envelope.
 */
export const readRawPayloadFromClipboard = async (): Promise<string | undefined> => {
  const clipboard = await navigator.clipboard.read();
  if (clipboard.length === 0) {
    return undefined;
  }
  const html = await getFromClipboard(clipboard[0], 'text/html');
  if (html) {
    const parser = new DOMParser();
    const clipboardElement = parser
      .parseFromString(html, 'text/html')
      ?.getElementById('canvas-clipboard');
    return clipboardElement?.dataset?.canvasClipboard;
  }

  return undefined;
};

export const getPlainTextFromClipboardItem = async (
  item: ClipboardItem,
): Promise<string | undefined> => {
  const content = await getFromClipboard(item, 'text/plain');
  if (!content) {
    return;
  }
  return content;
};

const extractDataFromClipboardItems = async (
  data: ClipboardItem[],
  shouldExtractFiles: boolean,
): Promise<{
  html: string | undefined;
  plainText: string | undefined;
  files: File[];
}> => {
  let html: string | undefined;
  let plainText: string | undefined;
  const files: File[] = [];
  for (const item of data) {
    if (!html) {
      html = await getFromClipboard(item, 'text/html');
    }

    if (!plainText) {
      plainText = await getFromClipboard(item, 'text/plain');
    }

    if (shouldExtractFiles) {
      for (const type of item.types) {
        if (type !== 'text/html' && type !== 'text/plain') {
          const blob = await item.getType(type);
          const file = new File([blob], '', { type });
          files.push(file);
        }
      }
    }
  }

  return {
    html,
    plainText,
    files,
  };
};

const handleFormattedTableHtml = async (
  html: string | undefined,
  options?: {
    onPasteSuccess?: (type: string, elementData?: ElementClipboardData[]) => void;
    measureTableCellContentHeight?: ClipboardTableCellMeasurementFn;
  },
): Promise<PasteOrDropPayload | undefined> => {
  const isWhiteboardsTablesEnabled = getExperimentValue(
    'whiteboardsTablesExperiment',
    'enabled',
    {
      fireExperimentExposure: true,
    },
  );

  if (!html || !isWhiteboardsTablesEnabled) {
    return undefined;
  }

  const tableData = await handleFormattedTable(html, {
    measureCellContentHeight: options?.measureTableCellContentHeight,
  });
  if (!tableData) {
    return undefined;
  }

  options?.onPasteSuccess?.('text/html', tableData);
  return {
    elementData: tableData,
    needsResize: false,
  };
};

export type PasteOrDropEventCaptureListenerOptions = {
  onUnsupportedContent: (type: string) => void;
  onPasteSuccess: (type: string, elementData?: ElementClipboardData[]) => void;
  measureTableCellContentHeight?: ClipboardTableCellMeasurementFn;
  customPayloadHandler?: (decoded: unknown) => boolean;
};

export const composePastePayloadFromClipboardItem = async (
  item: ClipboardItem,
  options?: Pick<PasteOrDropEventCaptureListenerOptions, 'measureTableCellContentHeight'>,
): Promise<PasteOrDropPayload | undefined> => {
  // Our format has highest precedence when pasting
  const elementData = await getCanvasPayloadFromClipboardItem(item);
  if (elementData) {
    return {
      elementData,
      needsResize: false,
    };
  }

  const html = await getFromClipboard(item, 'text/html');
  if (html) {
    const confluenceImageResult = parseConfluenceImageFromClipboardHtml(html);
    if (confluenceImageResult.kind !== 'notConfluenceImage') {
      return createConfluencePastePayload(confluenceImageResult);
    }
  }

  const tablePayload = await handleFormattedTableHtml(html, {
    measureTableCellContentHeight: options?.measureTableCellContentHeight,
  });
  if (tablePayload) {
    return tablePayload;
  }

  // Next, try to paste media from the clipboard
  const imageData = await getImageFromClipboardItem(item);
  if (imageData) {
    return {
      elementData: [imageData],
      needsResize: true,
    };
  }

  const rawText = await getPlainTextFromClipboardItem(item);
  if (!rawText) {
    return;
  }

  // If we can't get data using our format, fall back to plain text
  return handlePlainText(rawText);
};

/**
 * Gets data straight from a 'paste' OS event
 * and parses it using our clipboard format, or falls back to plain text
 * Not to be used for synthetic events.
 * @note This is the old pasteOrDropEventCaptureListener that is used when improvedCopyPaste flag is not enabled
 */
const legacyPasteOrDropEventCaptureListener = async (
  data: DataTransfer | null,
  options?: PasteOrDropEventCaptureListenerOptions,
): Promise<PasteOrDropPayload | undefined> => {
  const html = data?.getData('text/html');
  const plainText = data?.getData('text/plain');

  //Check experiment value
  const isUXHealthExperimentEnabled = getExperimentValue('uxHealthDec2025', 'enabled', {
    fireExperimentExposure: false,
  });

  // Check if there is no HTML first to avoid having to go through the HTML
  // and file parsing logic.
  if (!html && plainText) {
    return handlePlainText(plainText, options?.onPasteSuccess);
  }

  if (html) {
    // Our format (canvas payload) has highest precedence when pasting
    const payloadFromHtml = parsePastePayload(html);
    if (payloadFromHtml) {
      options?.onPasteSuccess('text/html', payloadFromHtml);
      return {
        elementData: payloadFromHtml,
        needsResize: false,
      };
    }

    if (options?.customPayloadHandler) {
      const payload = extractCanvasPayload(html);
      if (payload && options.customPayloadHandler(payload)) {
        return undefined;
      }
    }

    const confluenceImageResult = parseConfluenceImageFromClipboardHtml(html);
    if (confluenceImageResult.kind !== 'notConfluenceImage') {
      return createConfluencePastePayload(confluenceImageResult, options);
    }

    const tablePayload = await handleFormattedTableHtml(html, {
      onPasteSuccess: options?.onPasteSuccess,
      measureTableCellContentHeight: options?.measureTableCellContentHeight,
    });
    if (tablePayload) {
      return tablePayload;
    }

    // We know there is HTML and it is not a special canvas payload,
    // so it will be either normal HTML or prosemirror HTML
    const smartLinkData = tryParseAsSmartLink(plainText, options?.onPasteSuccess);
    if (smartLinkData) {
      return smartLinkData;
    }
  }

  // Next, try to paste media from the clipboard
  // NOTE: Accessing data.files can be very slow, only do it once!
  const importableFiles: Blob[] = [];
  let files: File[] = [];

  if (isUXHealthExperimentEnabled) {
    // Check for files in the data.files property first
    if (data?.files && data.files.length > 0) {
      // Use FileList for traditional file drops and existing functionality
      files = Array.from(data.files);
    }
    // If data.files is empty, check for files in the data.items property (used by Google)
    else if (data?.items && data.items.length > 0) {
      files = await handleGoogleImages(data.items, files);
    }
  } else {
    if (data?.files !== undefined) {
      for (let i = 0; i < data.files.length; i++) {
        const file = data.files.item(i);
        if (file !== null) {
          files.push(file);
        }
      }
    }
  }

  if (files !== undefined) {
    const parsePromises = [];
    for (const file of files) {
      if (file !== null && file !== undefined) {
        if (isSupportedImageMimeType(file.type)) {
          parsePromises.push(imageBlobToPasteData(file));
        } else if (isSupportedFileImportMimeType(file.type)) {
          importableFiles.push(file);
        } else {
          options?.onUnsupportedContent(file.type);
        }
      }
    }

    // Start importing files. We don't do it here since it's a potentially slow
    // operation, and we only process a single file to avoid trying to process too much
    // at once.
    if (importableFiles.length > 0) {
      commandService.send(ImportFileCommand, {
        blob: importableFiles[0],
        source: 'board',
        fileSource: 'clipboard',
      });
    }

    const imageList = (await Promise.all(parsePromises)).filter(
      Boolean,
    ) as ImagePasteData[];
    if (imageList.length > 0) {
      for (const image of imageList) {
        options?.onPasteSuccess(image.mimeType, imageList);
      }
      arrangeImages(imageList);
      return {
        elementData: imageList,
        needsResize: true,
      };
    }
  }

  // If we can't get data using our format, fall back to plain text
  return handlePlainText(plainText, options?.onPasteSuccess);
};

/**
 * Gets data straight from a 'paste' OS event or clipboard items
 * and parses it using our clipboard format, or falls back to plain text
 * @param data - Data from a 'paste' OS event or clipboard items
 * @param options - Optional callback functions for handling unsupported content and paste success
 * @returns A promise containing the paste or drop payload, or undefined if no data is found
 */
export const pasteOrDropEventCaptureListener = async (
  data: DataTransfer | ClipboardItem[] | null,
  options?: PasteOrDropEventCaptureListenerOptions,
): Promise<PasteOrDropPayload | undefined> => {
  if (!data) {
    return handlePlainText(undefined, options?.onPasteSuccess);
  }

  let html: string | undefined;
  let plainText: string | undefined;
  // NOTE: Accessing files can be very slow, only do it once!
  let files: File[] = [];
  let items: DataTransferItemList | null = null;

  // Check experiment value
  const isUXHealthExperimentEnabled = getExperimentValue('uxHealthDec2025', 'enabled', {
    fireExperimentExposure: false,
  });

  if (data instanceof DataTransfer) {
    html = data.getData('text/html') || undefined;
    plainText = data.getData('text/plain') || undefined;
    items = data.items || null;
    // data.files is read below after trying to pass html and text
  } else {
    const extracted = await extractDataFromClipboardItems(
      data as ClipboardItem[],
      isUXHealthExperimentEnabled,
    );
    html = extracted.html;
    plainText = extracted.plainText;
    files = extracted.files;
  }

  // Check if there is no HTML first to avoid having to go through the HTML
  // and file parsing logic.
  if (!html && plainText) {
    return handlePlainText(plainText, options?.onPasteSuccess);
  } else if (html) {
    // Our format (canvas payload) has highest precedence when pasting
    const payloadFromHtml = parsePastePayload(html);
    if (payloadFromHtml) {
      options?.onPasteSuccess('text/html', payloadFromHtml);
      return {
        elementData: payloadFromHtml,
        needsResize: false,
      };
    }
    if (options?.customPayloadHandler) {
      const payload = extractCanvasPayload(html);
      if (payload && options.customPayloadHandler(payload)) {
        return undefined;
      }
    }

    const tablePayload = await handleFormattedTableHtml(html, {
      onPasteSuccess: options?.onPasteSuccess,
      measureTableCellContentHeight: options?.measureTableCellContentHeight,
    });
    if (tablePayload) {
      return tablePayload;
    }

    const confluenceResult = parseConfluenceImageFromClipboardHtml(html);
    if (confluenceResult.kind === 'notConfluenceImage') {
      // Requiring plain text prevents ProseMirror from interpreting image-only HTML
      // supplied by sources such as Google as formatted text.
      if (plainText) {
        const contentPayload = handleHtmlText(
          html,
          plainText,
          options?.onPasteSuccess,
          true,
        );
        if (contentPayload) {
          return contentPayload;
        }
      }
    } else {
      const contentPayload = handleHtmlText(
        confluenceResult.htmlWithoutImages,
        undefined,
        undefined,
        true,
      );
      return createConfluencePastePayload(confluenceResult, {
        ...options,
        contentPayload,
      });
    }
  }

  if (data instanceof DataTransfer) {
    if (isUXHealthExperimentEnabled) {
      // Check for files in the data.files property first
      if (data.files && data.files.length > 0) {
        // Use FileList for traditional file drops and existing functionality
        files = Array.from(data.files);
      }
      // If data.files is empty, check for files in the data.items property (used by Google)
      else if (items && items.length > 0) {
        files = await handleGoogleImages(items, files);
      }
    } else {
      if (data.files !== undefined) {
        for (let i = 0; i < data.files.length; i++) {
          const file = data.files.item(i);
          if (file !== null) {
            files.push(file);
          }
        }
      }
    }
  }

  // Next, try to paste media from the clipboard
  const imageList = await handleDataFiles(files, options);
  if (imageList.length > 0) {
    for (const image of imageList) {
      options?.onPasteSuccess(image.mimeType, imageList);
    }
    arrangeImages(imageList);
    return {
      elementData: imageList,
      needsResize: true,
    };
  }

  // If we can't get data using our format, fall back to plain text
  return handlePlainText(plainText, options?.onPasteSuccess);
};

const handleDataFiles = async (
  files: File[] | null,
  options?: PasteOrDropEventCaptureListenerOptions,
): Promise<ImagePasteData[]> => {
  if (!files) {
    return [];
  }
  const importableFiles: Blob[] = [];
  const parsePromises = [];

  for (const file of files) {
    if (file !== null && file !== undefined) {
      if (isSupportedImageMimeType(file.type)) {
        parsePromises.push(imageBlobToPasteData(file));
      } else if (isSupportedFileImportMimeType(file.type)) {
        importableFiles.push(file);
      } else {
        options?.onUnsupportedContent(file.type);
      }
    }
  }

  // Start importing files. We don't do it here since it's a potentially slow
  // operation, and we only process a single file to avoid trying to process too much
  // at once.
  if (importableFiles.length > 0) {
    commandService.send(ImportFileCommand, {
      blob: importableFiles[0],
      source: 'board',
      fileSource: 'clipboard',
    });
  }

  const imageList = (await Promise.all(parsePromises)).filter(
    Boolean,
  ) as ImagePasteData[];
  return imageList;
};

const handleGoogleImages = async (
  dataItems: DataTransferItemList,
  files: File[],
): Promise<File[]> => {
  for (let i = 0; i < dataItems.length; i++) {
    const item = dataItems[i];
    if (item.kind === 'string') {
      await new Promise<void>((resolve) => {
        item.getAsString(async (htmlString) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlString, 'text/html');
          const images = doc.querySelectorAll('img');
          // in theory this should work for multiple images but certain services, like Google, do not provide image data if you copy
          // more then one image. Instead they choose to provide GUIDs which we cannot use. This is why we can only deal with a single
          // image from google
          for (const img of images) {
            const src = img.src;
            if (src && src.startsWith('data:')) {
              try {
                const response = await fetch(src);
                const blob = await response.blob();
                const filename = img.getAttribute('title') || 'pasted-image';
                const file = new File([blob], filename, { type: blob.type });
                files.push(file);
              } catch (err) {
                captureException(err, {
                  level: 'error',
                  tags: { captureLocation: 'pasteOrDropEventCaptureListener' },
                });
              }
            }
          }
          resolve();
        });
      });
    }
  }
  return files;
};

export const pasteEventCaptureListener = (
  evt: ClipboardEvent,
  options?: PasteOrDropEventCaptureListenerOptions,
) => {
  const isImprovedCopyPasteFlagEnabled = isFeatureFlagEnabled('improvedCopyPasteFlag');
  let isImprovedCopyPasteGateEnabled = false;
  // We only want the user to have access to the new function if the flag is enabled AND
  // the user is a part of the experiment. We also only want to fire the experiment
  // exposure event if the feature flag is enabled.
  if (isImprovedCopyPasteFlagEnabled) {
    isImprovedCopyPasteGateEnabled = getExperimentValue(
      'improvedCopyPasteGate',
      'isEnabled',
      {
        fireExperimentExposure: true,
      },
    );
  }

  return isImprovedCopyPasteGateEnabled
    ? pasteOrDropEventCaptureListener(evt?.clipboardData, options)
    : legacyPasteOrDropEventCaptureListener(evt?.clipboardData, options);
};

export const dropEventCaptureListener = (
  evt: DragEvent,
  options?: PasteOrDropEventCaptureListenerOptions,
) => {
  // We only want the user to have access to the new function if the flag is enabled AND
  // the user is a part of the experiment. We don't also fire experiment exposure since
  // drop events aren't a metric we're measuring.
  const isImprovedCopyPasteFlagEnabled = isFeatureFlagEnabled('improvedCopyPasteFlag');
  const isImprovedCopyPasteGateEnabled = getExperimentValue(
    'improvedCopyPasteGate',
    'isEnabled',
    {
      fireExperimentExposure: false,
    },
  );

  return isImprovedCopyPasteGateEnabled && isImprovedCopyPasteFlagEnabled
    ? pasteOrDropEventCaptureListener(evt?.dataTransfer, options)
    : legacyPasteOrDropEventCaptureListener(evt?.dataTransfer, options);
};
